import {
  getEquipment,
  IEquipmentPrevOrder,
  getAvailableItems,
  IEquipmentAvailableItems,
} from 'API';
import { useAppSelector } from 'Hooks';
import { useLoader } from 'Hooks/useLoader';
import { createContext, FC, useCallback, useEffect, useState } from 'react';

interface IEquipmentContext {
  recentOrders: IEquipmentPrevOrder[];
  setRecentOrders: (recentOrders: IEquipmentPrevOrder[]) => void;
  availableItems: IEquipmentAvailableItems;
  setAvailableItems: (availableItems: IEquipmentAvailableItems) => void;
  isLoading: boolean;
  loader: React.ReactNode;
  getAndSetEquipment: (page?: number, size?: number) => void;
  page: number;
  setPage: (page: number) => void;
}

export const EquipmentContext = createContext<IEquipmentContext>({
  recentOrders: [],
  setRecentOrders: () => {},
  availableItems: { caseNumber: '', items: [] },
  setAvailableItems: () => {},
  getAndSetEquipment: () => {},
  isLoading: true,
  loader: null,
  page: 1,
  setPage: () => {},
});

interface IEquipmentProvider {}

export const EquipmentProvider: FC<IEquipmentProvider> = (props) => {
  const [recentOrders, setRecentOrders] = useState<IEquipmentPrevOrder[]>([]);
  const [page, setPage] = useState(1);
  const [availableItems, setAvailableItems] =
    useState<IEquipmentAvailableItems>({ caseNumber: '', items: [] });
  const { isLoading, setIsLoading, loader } = useLoader();

  const imposterId = useAppSelector(
    ({ imposter: { imposterId } }) => imposterId
  );
  const getAndSetEquipment = async () => {
    const equipmentData = await getEquipment();
    if (equipmentData) {
      setRecentOrders(equipmentData);
    }
  };

  const getAvailableOrderItems = useCallback(async () => {
    const availableItemsData = await getAvailableItems();
    if (availableItemsData) {
      setAvailableItems(availableItemsData);
    }
    setIsLoading(false);
  }, [setAvailableItems, setIsLoading]);

  useEffect(() => {
    setIsLoading(true);
    getAndSetEquipment();
    getAvailableOrderItems();
  }, [getAvailableOrderItems, imposterId, setIsLoading]);

  return (
    <EquipmentContext.Provider
      value={{
        recentOrders,
        setRecentOrders,
        availableItems,
        getAndSetEquipment,
        setAvailableItems,
        isLoading,
        loader,
        page,
        setPage,
      }}
    >
      {isLoading ? loader : props.children}
    </EquipmentContext.Provider>
  );
};
